import axios from 'axios'
import firebase from 'firebase/app'
import 'firebase/auth'
import { isPaymentBankBlocked } from '@/utils/paymentBankAvailability'

const FUNCTIONS_BASE = 'https://asia-east2-time2-lucky.cloudfunctions.net'
const FINAL_STATUSES = ['SUCCESS', 'FAILED', 'CANCELLED', 'CREATE_FAILED', 'MANUAL_REVIEW']

function requestId () {
  return `kisspay-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`
}

function paymentUrl (value) {
  try {
    const url = new URL(String(value || '').trim())
    return url.protocol === 'https:' ? url.href : ''
  } catch (error) {
    return ''
  }
}

export default {
  data () {
    return {
      kisspayConfig: {
        enabled: false,
        depositEnabled: false,
        depositMin: 300,
        depositMax: 200000,
        availableNow: true,
        visibleToCodes: [],
        blockedBanks: []
      },
      kisspayConfigLoaded: false,
      kisspayTransaction: null,
      kisspayOrderId: '',
      kisspayRedirectUrl: '',
      kisspayClientRequestId: '',
      kisspayStatusTimer: null,
      kisspayStatusChecking: false,
      kisspayCompletionHandled: false
    }
  },
  computed: {
    hasActiveKissPayDeposit () {
      return Boolean(this.kisspayOrderId && this.kisspayTransaction)
    },
    kisspayPayableAmount () {
      const transaction = this.kisspayTransaction || {}
      const amount = Number(transaction.amount_payable || transaction.actual_amount || 0)
      return Number.isFinite(amount) && amount > 0 ? amount : null
    },
    kisspayDepositRangeText () {
      return `${Number(this.kisspayConfig.depositMin || 300).toLocaleString()}-${Number(this.kisspayConfig.depositMax || 200000).toLocaleString()}`
    },
    kisspayVisibleToUser () {
      const visible = Array.isArray(this.kisspayConfig.visibleToCodes)
        ? this.kisspayConfig.visibleToCodes.map(code => String(code || '').trim().toUpperCase()).filter(Boolean)
        : []
      const code = String((this.user && this.user.code) || '').trim().toUpperCase()
      return visible.includes('*') || Boolean(code && visible.includes(code))
    },
    kisspaySelectedDepositAccount () {
      const accounts = typeof this.getApprovedAccounts === 'function' ? this.getApprovedAccounts() : []
      return this.selectedDepositAccount || accounts[0] || null
    },
    kisspayAvailableForSelectedAccount () {
      return !this.isKissPayBlockedDepositAccount(this.kisspaySelectedDepositAccount)
    }
  },
  watch: {
    'user.key': {
      immediate: true,
      handler (userKey) {
        if (!userKey) return
        this.loadKissPayConfig()
        this.restoreKissPayDeposit()
      }
    }
  },
  beforeDestroy () {
    this.stopKissPayStatusPolling()
  },
  methods: {
    async kisspayHeaders () {
      const currentUser = firebase.auth().currentUser
      if (!currentUser) throw new Error('กรุณาเข้าสู่ระบบใหม่')
      return { Authorization: `Bearer ${await currentUser.getIdToken()}` }
    },
    async loadKissPayConfig () {
      try {
        const response = await axios.get(`${FUNCTIONS_BASE}/kisspayPublicConfig`, { timeout: 10000 })
        if (response.data && response.data.success && response.data.data) {
          this.kisspayConfig = { ...this.kisspayConfig, ...response.data.data }
        }
      } catch (error) {
        console.warn('KissPay public config unavailable:', error && error.message ? error.message : error)
      } finally {
        this.kisspayConfigLoaded = true
      }
    },
    isKissPayAmountAllowed (amount) {
      const value = Number(amount || 0)
      const min = Number(this.kisspayConfig.depositMin || 300)
      const max = Number(this.kisspayConfig.depositMax || 200000)
      return Number.isInteger(value) && value >= min && (max <= 0 || value <= max)
    },
    isKissPayBlockedDepositAccount (account) {
      const bank = account && (account.bank || account.bank_name)
      return String(bank || '').trim().toLowerCase().replace(/[\s_-]+/g, '') === 'truewallet' ||
        isPaymentBankBlocked(this.kisspayConfig.blockedBanks, account)
    },
    async tryKissPayDeposit (selectedAccount) {
      if (!this.kisspayConfigLoaded) await this.loadKissPayConfig()
      if (this.isKissPayBlockedDepositAccount(selectedAccount)) return false
      if (!this.kisspayConfig.enabled || !this.kisspayConfig.depositEnabled || this.kisspayConfig.availableNow === false || !this.kisspayVisibleToUser) return false
      const amount = Number.parseInt(this.inputDepositAmount, 10) || 0
      if (!this.isKissPayAmountAllowed(amount)) throw new Error(`KissPay รองรับยอดฝาก ${this.kisspayDepositRangeText} บาทต่อรายการเท่านั้น`)
      const clientRequestId = this.prepareKissPayRequestId(amount, selectedAccount)
      let response
      try {
        response = await axios.post(`${FUNCTIONS_BASE}/kisspayCreateDeposit`, {
          userKey: this.user.key,
          amount,
          account: selectedAccount,
          bonus: this.gotpro ? 'Y' : 'N',
          clientRequestId
        }, { headers: await this.kisspayHeaders(), timeout: 45000 })
      } catch (error) {
        const status = Number(error && error.response && error.response.status)
        if (status > 0 && status < 500) this.clearKissPayDepositCache()
        throw new Error(this.kisspayErrorMessage(error))
      }
      if (response.data && response.data.manualReview) {
        const pending = response.data.data || {}
        if (pending.order_id) this.trackKissPayPendingDeposit({
          ...pending,
          amount_requested: amount,
          client_request_id: clientRequestId,
          account_fingerprint: this.kisspayAccountFingerprint(selectedAccount)
        })
        throw new Error('KissPay กำลังตรวจสอบรายการเดิม กรุณาอย่าสร้างรายการซ้ำและติดต่อแอดมิน')
      }
      const data = response.data && response.data.data
      if (!response.data || !response.data.success || !data || !data.order_id || !(data.redirect_url || data.showurl)) {
        throw new Error((response.data && response.data.error) || 'KissPay response ไม่ครบถ้วน')
      }
      this.depositAmount = amount
      this.depositAmountWithDecimal = amount.toLocaleString('en-US')
      this.depositDecimal = '00'
      this.assignedBank = null
      this.paybridgeMatch = null
      if (!this.applyKissPayTransaction({
        ...data,
        amount_requested: amount,
        status: data.status || 'PENDING',
        client_request_id: clientRequestId,
        account_fingerprint: this.kisspayAccountFingerprint(selectedAccount)
      })) {
        throw new Error('ไม่สามารถบันทึกรายการฝาก KissPay ได้')
      }
      return true
    },
    kisspayErrorMessage (error) {
      const payload = error && error.response && error.response.data
      return (payload && payload.error) || (error && error.message) || 'KissPay ไม่สามารถสร้างรายการฝากได้ กรุณาลองใหม่'
    },
    kisspayStorageKey () {
      return `time2lucky_kisspay_deposit_${(this.user && this.user.key) || 'guest'}`
    },
    kisspayAccountFingerprint (account) {
      return [account && (account.bank || account.bank_name), account && (account.account_no || account.accountNo || account.account)]
        .map(value => String(value || '').trim().toLowerCase().replace(/[\s_-]+/g, ''))
        .join('|')
    },
    prepareKissPayRequestId (amount, account) {
      const fingerprint = this.kisspayAccountFingerprint(account)
      let cached = null
      if (typeof window !== 'undefined' && window.localStorage) {
        try { cached = JSON.parse(localStorage.getItem(this.kisspayStorageKey()) || 'null') } catch (error) {}
      }
      const reusable = cached && cached.client_request_id &&
        Number(cached.amount || 0) === Number(amount || 0) &&
        String(cached.account_fingerprint || '') === fingerprint &&
        Date.now() - Number(cached.stored_at || 0) < 15 * 60 * 1000
      this.kisspayClientRequestId = reusable ? cached.client_request_id : requestId()
      if (!reusable && typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem(this.kisspayStorageKey(), JSON.stringify({
          client_request_id: this.kisspayClientRequestId,
          amount: Number(amount || 0),
          account_fingerprint: fingerprint,
          stored_at: Date.now()
        }))
      }
      return this.kisspayClientRequestId
    },
    persistKissPayDeposit (transaction) {
      if (typeof window === 'undefined' || !window.localStorage || !transaction) return
      localStorage.setItem(this.kisspayStorageKey(), JSON.stringify({
        order_id: transaction.order_id || this.kisspayOrderId,
        redirect_url: transaction.redirect_url || transaction.showurl || this.kisspayRedirectUrl,
        client_request_id: this.kisspayClientRequestId || transaction.client_request_id || '',
        account_fingerprint: transaction.account_fingerprint || '',
        amount: Number(transaction.amount_requested || transaction.amount || this.depositAmount || 0),
        stored_at: Date.now()
      }))
    },
    clearKissPayDepositCache () {
      if (typeof window !== 'undefined' && window.localStorage) localStorage.removeItem(this.kisspayStorageKey())
    },
    applyKissPayTransaction (transaction) {
      if (!transaction) return false
      const orderId = transaction.order_id || ''
      const redirectUrl = paymentUrl(transaction.redirect_url || transaction.showurl)
      if (!orderId || !redirectUrl) return false
      this.kisspayTransaction = { ...(this.kisspayTransaction || {}), ...transaction }
      this.kisspayOrderId = orderId
      this.kisspayRedirectUrl = redirectUrl
      this.kisspayClientRequestId = transaction.client_request_id || this.kisspayClientRequestId
      this.depositAmount = Number(transaction.amount_requested || transaction.amount || this.depositAmount || 0)
      this.depositAmountWithDecimal = this.kisspayPayableAmount === null ? '' : this.kisspayPayableAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      this.assignedBank = null
      this.persistKissPayDeposit(transaction)
      this.startKissPayStatusPolling(orderId)
      return true
    },
    trackKissPayPendingDeposit (transaction) {
      if (!transaction || !transaction.order_id) return false
      this.kisspayTransaction = { ...(this.kisspayTransaction || {}), ...transaction }
      this.kisspayOrderId = transaction.order_id
      this.kisspayRedirectUrl = paymentUrl(transaction.redirect_url || transaction.showurl)
      this.kisspayClientRequestId = transaction.client_request_id || this.kisspayClientRequestId
      this.depositAmount = Number(transaction.amount_requested || transaction.amount || this.depositAmount || 0)
      this.persistKissPayDeposit(transaction)
      this.startKissPayStatusPolling(transaction.order_id)
      return true
    },
    startKissPayStatusPolling (orderId) {
      if (!orderId) return
      this.stopKissPayStatusPolling()
      this.kisspayCompletionHandled = false
      const poll = () => this.fetchKissPayDepositStatus(orderId)
      poll()
      this.kisspayStatusTimer = setInterval(poll, 4000)
    },
    stopKissPayStatusPolling () {
      if (this.kisspayStatusTimer) clearInterval(this.kisspayStatusTimer)
      this.kisspayStatusTimer = null
    },
    async fetchKissPayDepositStatus (orderId) {
      if (!orderId || !(this.user && this.user.key) || this.kisspayCompletionHandled || this.kisspayStatusChecking) return null
      this.kisspayStatusChecking = true
      try {
        const response = await axios.post(`${FUNCTIONS_BASE}/kisspayGetDepositStatus`, {
          userKey: this.user.key,
          orderId
        }, { headers: await this.kisspayHeaders(), timeout: 15000 })
        const transaction = response.data && response.data.data
        if (!response.data || !response.data.success || !transaction) return null
        this.kisspayTransaction = { ...(this.kisspayTransaction || {}), ...transaction }
        const status = String(transaction.status || '').toUpperCase()
        if (status === 'SUCCESS') this.handleKissPayDepositSuccess(transaction)
        if (['FAILED', 'CREATE_FAILED', 'MANUAL_REVIEW'].includes(status)) this.handleKissPayDepositFailed(transaction, status)
        return transaction
      } catch (error) {
        return null
      } finally {
        this.kisspayStatusChecking = false
      }
    },
    async restoreKissPayDeposit () {
      if (typeof window === 'undefined' || !window.localStorage || !(this.user && this.user.key)) return false
      try {
        const cached = JSON.parse(localStorage.getItem(this.kisspayStorageKey()) || 'null')
        if (!cached || !cached.order_id) return false
        const response = await axios.post(`${FUNCTIONS_BASE}/kisspayGetDepositStatus`, {
          userKey: this.user.key,
          orderId: cached.order_id
        }, { headers: await this.kisspayHeaders(), timeout: 15000 })
        const statusData = response.data && response.data.data
        if (!response.data || !response.data.success || !statusData) {
          this.clearKissPayDepositCache()
          return false
        }
        const transaction = { ...cached, ...statusData, order_id: cached.order_id }
        const status = String(transaction.status || '').toUpperCase()
        if (status === 'SUCCESS') {
          this.handleKissPayDepositSuccess(transaction)
          return true
        }
        if (['FAILED', 'CREATE_FAILED', 'MANUAL_REVIEW'].includes(status)) {
          this.handleKissPayDepositFailed(transaction, status)
          return true
        }
        if (FINAL_STATUSES.includes(status)) {
          this.clearKissPayDepositCache()
          return false
        }
        return (transaction.redirect_url || transaction.showurl)
          ? this.applyKissPayTransaction(transaction)
          : this.trackKissPayPendingDeposit(transaction)
      } catch (error) {
        this.clearKissPayDepositCache()
        return false
      }
    },
    handleKissPayDepositSuccess (transaction) {
      if (this.kisspayCompletionHandled) return
      this.kisspayCompletionHandled = true
      this.stopKissPayStatusPolling()
      this.clearKissPayDepositCache()
      const amount = Number(transaction.actual_amount || transaction.amount_requested || this.depositAmount || 0)
      this.resetKissPayDepositState()
      if (typeof window !== 'undefined' && window.$) window.$('#depositModal').modal('hide')
      setTimeout(() => this.$swal({ icon: 'success', title: 'ฝากเงินสำเร็จ', text: `ระบบเติมเครดิต ${amount.toLocaleString()} บาทเรียบร้อยแล้ว` }), 200)
    },
    handleKissPayDepositFailed (transaction, status) {
      if (this.kisspayCompletionHandled) return
      this.kisspayCompletionHandled = true
      this.stopKissPayStatusPolling()
      this.clearKissPayDepositCache()
      const reason = transaction.failed_reason || transaction.credit_error || transaction.kisspay_error || ''
      this.resetKissPayDepositState()
      if (typeof window !== 'undefined' && window.$) window.$('#depositModal').modal('hide')
      setTimeout(() => this.$swal({
        icon: status === 'MANUAL_REVIEW' ? 'warning' : 'error',
        title: status === 'MANUAL_REVIEW' ? 'กำลังตรวจสอบรายการฝาก' : 'รายการฝากไม่สำเร็จ',
        text: reason || 'กรุณาทำรายการใหม่หรือติดต่อแอดมิน'
      }), 200)
    },
    resetKissPayDepositState () {
      this.kisspayTransaction = null
      this.kisspayOrderId = ''
      this.kisspayRedirectUrl = ''
      this.kisspayClientRequestId = ''
      this.kisspayStatusChecking = false
      this.kisspayCompletionHandled = false
    },
    async cancelKissPayDeposit (options = {}) {
      const orderId = this.kisspayOrderId
      if (!orderId) return
      if (this.$swal && !options.skipConfirmation) {
        const confirmation = await this.$swal({
          icon: 'warning',
          title: 'ยกเลิกรายการและเปลี่ยนยอดฝาก?',
          text: 'หากโอนไปแล้วอย่ายกเลิก กรุณารอระบบยืนยันรายการสำเร็จ',
          showCancelButton: true,
          confirmButtonText: 'ยืนยันยกเลิก',
          cancelButtonText: 'รอทำรายการต่อ'
        })
        if (!confirmation.isConfirmed) return
      }
      try {
        const response = await axios.post(`${FUNCTIONS_BASE}/kisspayCancelDeposit`, {
          userKey: this.user.key,
          orderId
        }, { headers: await this.kisspayHeaders(), timeout: 15000 })
        if (!response.data || !response.data.success) throw new Error((response.data && response.data.error) || 'ไม่สามารถยกเลิกรายการฝากได้')
        if (response.data.alreadyCompleted) {
          await this.fetchKissPayDepositStatus(orderId)
          return
        }
      } catch (error) {
        if (this.$swal) await this.$swal({ icon: 'error', title: 'ยังไม่สามารถสร้างรายการใหม่ได้', text: 'ระบบปิดรายการฝากเดิมไม่สำเร็จ กรุณาลองอีกครั้ง' })
        return
      }
      this.stopKissPayStatusPolling()
      this.clearKissPayDepositCache()
      this.resetKissPayDepositState()
      this.depositAmount = null
      this.depositAmountWithDecimal = null
      this.inputDepositAmount = null
    }
  }
}
